/* eslint-disable indent */
import {
    addDays,
    isEqual,
    isSameDay,
    isSameMonth,
    isSameYear,
    parseISO,
    startOfDay,
} from 'date-fns';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import startCase from 'lodash/startCase';
import queryString from 'query-string';

import { Blocks } from '../types/blocks';
import { Magazine } from '../types/entities';
import { IntlMessage } from '../types/shared';

// ============================================================================
// String Utilities
// ============================================================================

export const pascalCase = (str: string): string => startCase(camelCase(str)).replace(/ /g, '');

export const addNonBreakingSpaces = (str: string | null): string | null =>
    str !== null ? str.replace(/&nbsp;/g, '\u00a0') : null;

export const cleanExternalLinks = (str: string | null): string | null =>
    str !== null ? str.replace(/(<a\b[^<>]*href=['"]?http[^<>]+)>/gi, '$1 target="_blank">') : null;

// ============================================================================
// Object Transformation Utilities
// ============================================================================

export const snakeToCamel = <T = unknown>(obj: unknown): T => {
    if (obj === null || obj === undefined) {
        return obj as T;
    }

    if (obj instanceof Date) {
        return obj as T;
    }

    if (isArray(obj)) {
        return obj.map((item) => snakeToCamel(item)) as T;
    }

    if (isObject(obj) && !(obj instanceof Date)) {
        return Object.keys(obj as Record<string, unknown>).reduce(
            (acc, key) => ({
                ...acc,
                [camelCase(key)]: snakeToCamel((obj as Record<string, unknown>)[key]),
            }),
            {} as Record<string, unknown>,
        ) as T;
    }

    return obj as T;
};

// ============================================================================
// Component Utilities
// ============================================================================

export const getComponentFromName = (
    name: string,
    components: Record<string, unknown>,
    defaultComponent: unknown = null,
): unknown => {
    const componentName = name !== null ? pascalCase(name) : null;
    return components[componentName] || defaultComponent;
};

// ============================================================================
// Type Guards
// ============================================================================

export const isMessage = (message: unknown): message is IntlMessage =>
    isObject(message) && typeof (message as IntlMessage).id !== 'undefined';

// ============================================================================
// Filter Utilities
// ============================================================================

type FilterType = 'integer' | 'string' | 'bool' | 'boolean';

interface FilterShape {
    multiple?: boolean;
    type?: FilterType;
}

type FilterShapeMap = Record<string, FilterShape | true>;

export const castFilter = (value: string | number, type: FilterType): string | number | boolean => {
    if (type === 'integer') {
        return parseInt(String(value), 10);
    }
    if (type === 'string') {
        return String(value);
    }
    if (type === 'bool' || type === 'boolean') {
        return value === 'true' || value === '1' || value === 1;
    }
    return value;
};

export const getFiltersFromQuery = (
    query: Record<string, unknown> | null,
    shape: FilterShapeMap,
): Record<string, unknown> | null => {
    if (query === null) {
        return null;
    }
    const filters = Object.keys(shape).reduce(
        (allFilters, key) => {
            const filterShape: FilterShape = {
                multiple: true,
                type: 'integer',
                ...(shape[key] !== true ? (shape[key] as FilterShape) : {}),
            };
            let value = query[key] || null;
            if (isEmpty(value)) {
                return allFilters;
            }
            if (filterShape.multiple && !isArray(value)) {
                value = [value];
            }
            return {
                ...allFilters,
                [key]: isArray(value)
                    ? (value as Array<string | number>).map((val) =>
                          castFilter(val, filterShape.type || 'integer'),
                      )
                    : castFilter(value as string | number, filterShape.type || 'integer'),
            };
        },
        {} as Record<string, unknown>,
    );
    return Object.keys(filters).length > 0 ? filters : null;
};

export const getQueryStringFromFilters = (filters: Record<string, unknown>): string => {
    const query = Object.keys(filters).reduce(
        (allFilters, key) =>
            !isEmpty(filters[key])
                ? {
                      ...allFilters,
                      [key]: filters[key],
                  }
                : allFilters,
        {} as Record<string, unknown> | null,
    );
    return query !== null
        ? `?${queryString.stringify(filters, {
              arrayFormat: 'bracket',
          })}`
        : '';
};

// ============================================================================
// Page Utilities
// ============================================================================

interface PageWithBlocks {
    blocks?: Blocks | null;
}

export const lastPageBlockColor = (page: PageWithBlocks): string | null => {
    const blocks = page.blocks || null;
    if (blocks === null || blocks.length === 0) {
        return null;
    }
    const lastBlock = blocks[blocks.length - 1];
    if (lastBlock.type === 'suggested_events') {
        return 'grey';
    }
    if (lastBlock.type === 'suggested_contacts') {
        return 'white';
    }
    return null;
};

// ============================================================================
// Magazine Utilities
// ============================================================================

interface MagazinePage {
    items: Magazine[];
}

export const lastMagazineIsVideo = (magazines: MagazinePage[] | null): boolean => {
    if (magazines === null) {
        return false;
    }
    const items = magazines.reduce(
        (prevItems, itemsPage) => [...prevItems, ...itemsPage.items],
        [] as Magazine[],
    );
    const itemsCount = items.length;
    if (itemsCount === 0) {
        return false;
    }

    return (
        items[itemsCount - 1].type === 'magazine_video' ||
        items[itemsCount - 1].type === 'magazine_podcast' ||
        items[itemsCount - 1].type === 'magazine_photo'
    );
};

// ============================================================================
// Locale Utilities
// ============================================================================

type LocaleText = string | Record<string, string> | null;

// prettier-ignore
export const getLocaleText = (
    text: LocaleText,
    locale: string,
    defaultValue: string | null = null,
): string | null => (isString(text) || text === null
    ? text
    : get(text, locale, defaultValue));

// prettier-ignore
export const getLocaleTextWithFallback = (
    text: LocaleText,
    locale: string,
): string | null => (text !== null
    ? getLocaleText(text, locale, Object.values(text as Record<string, string>)[0] || null)
    : null);

// ============================================================================
// Style Utilities
// ============================================================================

export const generateItemStaggerStyle = (
    itemSelector: string,
    itemsPerPage: number,
    maxPage: number = 1,
): string => {
    const itemsStyles: string[] = [];
    for (let page = 0; page < maxPage; page += 1) {
        for (let i = 0; i < itemsPerPage; i += 1) {
            // prettier-ignore
            itemsStyles.push(`
                ${itemSelector}:nth-child(${(page * itemsPerPage) + i + 1}) {
                    opacity: 0;
                    animation-delay: ${0.1 * i}s;
                }
            `);
        }
    }
    return itemsStyles.join('\n');
};

export const prefixes = ['moz', 'webkit', 'ms', 'o'];

export const getPrefixedStyleProp = (prop: string): string => {
    const root = document.documentElement;
    for (let i = 0; i < prefixes.length; i += 1) {
        const prefixedProp = `${prefixes[i]}${prop.substr(0, 1).toUpperCase()}${prop.substr(1)}`;
        if (typeof (root.style as Record<string, unknown>)[prefixedProp] !== 'undefined') {
            return prefixedProp;
        }
    }
    return prop;
};

export const setPrefixedStyle = (el: HTMLElement, style: Record<string, string | number>): void => {
    Object.keys(style).forEach((key) => {
        (el.style as Record<string, string | number>)[getPrefixedStyleProp(key)] = style[key];
    });
};

// ============================================================================
// Date Utilities
// ============================================================================

interface DateParts {
    year: number;
    month: number;
    day: number;
}

interface TimeParts {
    hours: number;
    minutes: number;
    seconds: number;
}

const dateCache: Record<string, Date> = {};

export const parseDate = (dateStr: string): DateParts | null => {
    const dateParts = dateStr.split('-');
    return dateParts.length === 3
        ? {
              year: parseInt(dateParts[0], 10),
              month: parseInt(dateParts[1], 10),
              day: parseInt(dateParts[2], 10),
          }
        : null;
};

export const parseTime = (timeStr: string): TimeParts | null => {
    const timeParts = timeStr.split(':');
    return timeParts.length >= 2 && timeParts.length <= 3
        ? {
              hours: parseInt(timeParts[0], 10),
              minutes: parseInt(timeParts[1], 10),
              seconds: parseInt(timeParts[2] || '0', 10),
          }
        : null;
};

export function getEventDates(dates = null) {
    const {
        startDate: initialStartDate = null,
        endDate: initialEndDate = null,
        startDateTime = null,
        endDateTime = null,
        datesCount = 0,
    } = dates || {};

    const start = startDateTime !== null ? startDateTime : initialStartDate;
    const startDate = parseISO(start);

    const end = endDateTime !== null ? endDateTime : initialEndDate;
    const endDate = end !== null ? parseISO(end) : null;

    const range = endDate && !isSameDay(endDate, startDate);

    const startDay = startOfDay(startDate);
    const isStartOfDay = isEqual(startDate, startDay);

    const sameYear = endDate !== null && isSameYear(startDate, endDate);
    const sameMonth = endDate !== null && isSameMonth(startDate, endDate);

    const isTwoDates = datesCount !== null && datesCount === 2;
    const isTomorrow = endDate !== null ? isSameDay(addDays(startDate, 1), endDate) : false;
    const isAnd = isTomorrow || isTwoDates;

    return {
        start,
        end,
        startDate,
        endDate,
        range,
        isStartOfDay,
        sameYear,
        sameMonth,
        isAnd,
        datesCount,
    };
}

// ============================================================================
// Window Utilities
// ============================================================================

interface ScrollPosition {
    x: number;
    y: number;
}

interface WindowSize {
    width: number;
    height: number;
}

const currentScroll: ScrollPosition = {
    x: 0,
    y: 0,
};

const emptyObject = {};

export const getScroll = (): ScrollPosition => {
    const { pageXOffset = null, pageYOffset = null } = window || emptyObject;
    const doc = (document || emptyObject).documentElement || emptyObject;
    // prettier-ignore
    currentScroll.x = (pageXOffset || (doc as HTMLElement).scrollLeft || 0) - ((doc as HTMLElement).clientLeft || 0);
    // prettier-ignore
    currentScroll.y = (pageYOffset || (doc as HTMLElement).scrollTop || 0) - ((doc as HTMLElement).clientTop || 0);
    return currentScroll;
};

export const getWindowWidth = (): WindowSize => {
    const width =
        window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const height =
        window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    return { width, height };
};

export function filterQuery(query) {
    if (query === null) {
        return null;
    }
    Object.keys(query).forEach((key) => {
        const value = query[key];
        if (
            value === null ||
            value === undefined ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '')
        ) {
            delete query[key];
        }
    });
    return query;
}

// Remove params from an url
export function removeUrlParams(url: string): string | null {
    if (url === null || url === undefined || url.trim() === '') {
        return null;
    }
    const urlObj = new URL(url, window.location.origin);
    urlObj.search = '';
    return urlObj.toString();
}
